import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Calendar, Clock, MapPin, Phone, Loader, CheckCircle2, XCircle, HandHeart } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_LABEL = {
  central_bekaa: { ar: 'بقاع أوسط', en: 'Central Bekaa', fr: 'Bekaa central' },
  western_bekaa: { ar: 'بقاع غربي', en: 'Western Bekaa', fr: 'Bekaa occidental' },
};

export const PhysioBookingsPanel = () => {
  const { t, language } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState('');

  const fetchBookings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/physio/bookings`, { withCredentials: true });
      setBookings(r.data || []);
    } catch (err) {
      console.warn('physio bookings load failed:', err?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    const id = setInterval(fetchBookings, 20000);
    return () => clearInterval(id);
  }, [fetchBookings]);

  const handle = async (id, action) => {
    setActingId(id);
    try {
      await axios.post(`${API}/physio/bookings/${id}/${action}`, {}, { withCredentials: true });
      toast.success(action === 'approve'
        ? t('تمت الموافقة على الحجز', 'Booking approved', 'Réservation approuvée')
        : t('تم رفض الحجز', 'Booking declined', 'Réservation refusée'));
      await fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشلت العملية', 'Action failed', 'Échec'));
    } finally {
      setActingId('');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader className="animate-spin text-[#1565c0]" size={22} /></div>;
  }

  if (bookings.length === 0) {
    return (
      <Card className="p-6 text-center" data-testid="physio-bookings-empty">
        <HandHeart size={40} className="text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">
          {t('لا توجد طلبات علاج فيزيائي حالياً', 'No physiotherapy requests right now', 'Aucune demande pour le moment')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="physio-bookings-list">
      {bookings.map((b) => {
        const regionLabel = REGION_LABEL[b.region]?.[language] || b.region;
        const isPending = b.status === 'pending';
        return (
          <Card key={b._id} className="p-4 md:p-5" data-testid={`physio-booking-${b._id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h4 className="font-extrabold text-[#0d2c54] text-base md:text-lg">
                  {b.package_title_ar || b.package_title_en}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {b.package_sessions} {t('جلسات', 'sessions', 'séances')} · ${b.package_price_usd}
                </p>
              </div>
              <span
                className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${
                  b.status === 'approved' ? 'bg-green-100 text-green-700'
                  : b.status === 'declined' ? 'bg-red-100 text-red-700'
                  : 'bg-yellow-100 text-yellow-700'
                }`}
                data-testid={`physio-status-${b._id}`}
              >
                {b.status === 'approved' ? t('موافق عليه', 'Approved', 'Approuvé')
                  : b.status === 'declined' ? t('مرفوض', 'Declined', 'Refusé')
                  : t('بانتظار الموافقة', 'Pending', 'En attente')}
              </span>
            </div>

            <div className="space-y-1.5 text-sm text-gray-700">
              <p className="flex items-center gap-2">
                <Calendar size={14} className="text-[#1565c0]" />
                <span className="font-semibold">{b.preferred_date}</span>
                <Clock size={14} className="text-[#1565c0] ms-2" />
                <span className="font-semibold">{b.preferred_time}</span>
              </p>
              <p className="flex items-start gap-2">
                <MapPin size={14} className="text-[#1565c0] mt-0.5 flex-shrink-0" />
                <span>
                  <b>{regionLabel}</b> · {b.city} · {b.street}
                  {b.address_details ? ` · ${b.address_details}` : ''}
                </span>
              </p>
              <p className="flex items-center gap-2 text-gray-600">
                <span className="font-semibold">{b.patient_name}</span>
                {b.patient_phone && (
                  <a href={`tel:${b.patient_phone}`} className="flex items-center gap-1 text-[#1565c0] hover:underline">
                    <Phone size={12} /> {b.patient_phone}
                  </a>
                )}
              </p>
              {b.notes && (
                <p className="text-xs text-gray-500 bg-gray-50 rounded p-2 mt-2">
                  <b>{t('ملاحظات:', 'Notes:', 'Notes :')}</b> {b.notes}
                </p>
              )}
            </div>

            {isPending && (
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => handle(b._id, 'approve')}
                  disabled={actingId === b._id}
                  className="flex-1 bg-green-600 hover:bg-green-700 h-9 rounded-full font-bold text-sm"
                  data-testid={`approve-${b._id}`}
                >
                  <CheckCircle2 size={16} className="me-1" />
                  {t('موافقة', 'Approve', 'Approuver')}
                </Button>
                <Button
                  onClick={() => handle(b._id, 'decline')}
                  disabled={actingId === b._id}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 h-9 rounded-full font-bold text-sm"
                  data-testid={`decline-${b._id}`}
                >
                  <XCircle size={16} className="me-1" />
                  {t('رفض', 'Decline', 'Refuser')}
                </Button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default PhysioBookingsPanel;
